'use client';

import { OptimizedImage } from '@/components/images/optimized-image';
import { useNsfwStatusByUrl } from '@/hooks/use-nsfw-status-by-url';
import { heroCoverDisplayMode } from '@/lib/pattern-studio';
import { cn } from '@/lib/ui/utils';
import { useUploadTrackingStore } from '@/stores/upload-tracking';

export interface HeroBannerProps {
  /**
   * HTTPS URL to the user-uploaded hero. Null/empty → lib synthesises a
   * deterministic placeholder using `userId` as the seed. Accepts
   * `undefined` so callers reading from rolling-release DTOs (FR-023)
   * pass through without manual `?? null`.
   */
  heroUrl: string | null | undefined;
  /**
   * Used as the seed source for the lib manifest. Stable across refresh
   * because user ids are immutable. Pass `null` / `undefined` only when
   * the user record hasn't been created yet (onboarding pre-signup);
   * the component falls back to `displayName` so the placeholder still
   * varies per visible identity.
   */
  userId: string | null | undefined;
  /** Used as the seed fallback when `userId` is unavailable. */
  displayName: string | null | undefined;
  /**
   * LCP hint. Pass `true` for the in-viewport profile header so Next/Image
   * preloads. Defaults `false` for any list/below-the-fold context.
   */
  priority?: boolean;
  /** Extra classes for the outer 3:1 wrapper. Avoid layout overrides. */
  className?: string;
  /**
   * When `true`, clicking the banner opens the global lightbox preview.
   * Default `false` (the banner is a bare display surface — clickable
   * surfaces like the profile-settings edit trigger are owned by the
   * parent, e.g. `<EditableHero>`).
   */
  preview?: boolean;
  /**
   * When `true`, the underlying cover photo is rendered through the
   * NSFW-blur overlay until the viewer taps to reveal. Comes from the
   * SDK `coverNsfw` field on user/profile responses (spec 071). Default
   * `false` — pass through `user.coverNsfw ?? false` at every callsite.
   */
  nsfw?: boolean;
}

interface HeroBannerImageProps {
  coverUrl: string | null;
  seedKey: string;
  priority: boolean;
  preview: boolean;
  nsfw: boolean;
}

function HeroBannerImage(props: HeroBannerImageProps): React.ReactNode {
  const uploadTracked = useUploadTrackingStore((state) =>
    props.coverUrl === null ? false : state.urlToSha.has(props.coverUrl),
  );
  if (uploadTracked) return <TrackedHeroBannerImage {...props} />;
  return <HeroBannerImageContent {...props} />;
}

function TrackedHeroBannerImage(props: HeroBannerImageProps): React.ReactNode {
  const liveNsfw = useNsfwStatusByUrl(props.coverUrl).isFlagged;
  return <HeroBannerImageContent {...props} nsfw={props.nsfw || liveNsfw} />;
}

function HeroBannerImageContent({
  coverUrl,
  seedKey,
  priority,
  preview,
  nsfw,
}: HeroBannerImageProps): React.ReactNode {
  return (
    <OptimizedImage
      variant="hero"
      entity={{
        kind: 'cover',
        userId: seedKey,
        coverUrl,
        coverRender: heroCoverDisplayMode(coverUrl),
        nsfw,
      }}
      alt=""
      priority={priority}
      className="absolute inset-0 h-full w-full object-cover"
      imageClassName="object-cover"
      sizes="(max-width: 768px) 100vw, 640px"
      preview={preview}
      rounded=""
    />
  );
}

/**
 * `<HeroBanner>` — pure renderer for a user profile's 3:1 hero region.
 *
 * Dot Studio covers render live from `userId` (and optional SVG marker in
 * `heroUrl`). Legacy baked IPFS generative PNGs are ignored in favour of the
 * current engine. Photo uploads still use the passthrough lane.
 */
export function HeroBanner({
  heroUrl,
  userId,
  displayName,
  priority = false,
  className,
  preview = false,
  nsfw = false,
}: HeroBannerProps): React.ReactNode {
  const coverUrl: string | null =
    typeof heroUrl === 'string' && heroUrl.length > 0 ? heroUrl : null;
  // userId is the canonical seed source. When the user record isn't created
  // yet (onboarding pre-signup) we fall back to displayName so each visible
  // identity gets a stable look. Empty seed → lib uses palette default.
  const seedKey: string =
    typeof userId === 'string' && userId.length > 0
      ? userId
      : typeof displayName === 'string'
        ? displayName
        : '';

  return (
    <div
      className={cn('relative aspect-[3/1] w-full overflow-hidden', className)}
      data-testid="hero-banner"
    >
      <HeroBannerImage
        coverUrl={coverUrl}
        seedKey={seedKey}
        priority={priority}
        preview={preview}
        nsfw={nsfw}
      />
    </div>
  );
}
